// Modules.
import Vue from 'vue';
import { GetterTree, MutationTree, ActionTree, Module } from 'vuex';

// Stores.
import { RootState } from './RootState';

// Modèles.
import { ContratAchatCriteria } from '@/models/ContratAchatCriteria.model';
import { FactureCriteria } from '@/models/FactureCriteria.model';
import { IntrantCriteria } from '@/models/IntrantCriteria.model';
import { CommandeCriteria } from '@/models/CommandeCriteria.model';
import { UtilisateurProducteurCriteria } from '@/components/administration/utilisateurs/models/UtilisateurProducteurCriteria.model';
import { CriteriaBase } from '@/shared/models/CriteriaBase.model';
import { EnvoiVestaCriteria } from '@/models/vesta/EnvoiVestaCriteria.model';
import { DeclarationPosCriteria } from '../models/DeclarationPosCriteria';
import { MandatCriteria } from '@/models/MandatCriteria.model';

/**
 * Constantes pour les méthodes du store.
 */
export enum CriteriaStoreMethods {
    GET_CRITERIAS = 'GET_CRITERIAS',
    SET_CRITERIA = 'SET_CRITERIA',
    GET_CONTRATACHATIDS = 'GET_CONTRATACHATIDS',
    SET_CONTRATACHATIDS = 'SET_CONTRATACHATIDS',
}

/**
 * Enumérations des différents types de criteria.
 * */
export enum TypeCriteria {
    ContratAchat = 'ContratAchat',
    Facture = 'Facture',
    FactureErreur = 'FactureErreur',
    Commande = 'Commande',
    UtilisateurProducteur = 'UtilisateurProducteur',
    Intrants = 'Intrants',
    EnvoiVesta = 'EnvoiVesta',
    DeclarationPos = 'DeclarationPos',
    Mandat = 'Mandat'
}

/**
 * Méthode permettant de récupérer une valeur initiale de criteria.
 * */
export function getCriteriaInitial(typeCriteria: string): CriteriaBase {
    switch (typeCriteria) {
        case TypeCriteria.ContratAchat:
            return new ContratAchatCriteria();
        case TypeCriteria.Facture:
            return new FactureCriteria();
        case TypeCriteria.Commande:
            return new CommandeCriteria();
        case TypeCriteria.UtilisateurProducteur:
            return new UtilisateurProducteurCriteria();
        case TypeCriteria.Intrants:
            return new IntrantCriteria();
        case TypeCriteria.EnvoiVesta:
            return new EnvoiVestaCriteria();
        case TypeCriteria.DeclarationPos:
            return new DeclarationPosCriteria();
        case TypeCriteria.Mandat:
            return new MandatCriteria()
        default:
            // Ne devrait jamais arriver ici.
            return new CriteriaBase();
    }
}

/**
 * Classe de gestion critères de recherches enregistrés quand on navigue sur l'appli.
 */
export class CriteriaState {

    /**
     * Dictionnaire avec tous les criterias de l'appli à enregistrer.
     */
    public criterias: { [key: string]: CriteriaBase } = {};

    /**
     * L'ensemble des contrats d'achat (installations) liés aux factures.
     */
    public contratAchatIds: number[];

    public constructor() {

        this.contratAchatIds = [];

        // Initialisation du dictionnaire.
        for (const typeCriteria in TypeCriteria) {
            this.criterias[typeCriteria] = getCriteriaInitial(typeCriteria);
        }
    }
}

/**
 * Getters du store.
 */
const getters: GetterTree<CriteriaState, RootState> = {
    [CriteriaStoreMethods.GET_CRITERIAS]: (state) => state.criterias,
    [CriteriaStoreMethods.GET_CONTRATACHATIDS]: (state) => state.contratAchatIds,
};

/**
 * Mutateurs du store.
 */
const mutations: MutationTree<CriteriaState> = {

    /**
     * Set un criteria individuellement à partir du type de criteria et de sa valeur.
     */
    [CriteriaStoreMethods.SET_CRITERIA]: (state: CriteriaState, payload: { typeCriteria: TypeCriteria, value: CriteriaBase }) => Vue.set(state.criterias, payload.typeCriteria, payload.value),
    [CriteriaStoreMethods.SET_CONTRATACHATIDS]: (state: CriteriaState, payload: { key: 'contratAchatIds', value: number[] }) => Vue.set(state.contratAchatIds, payload.key, payload.value),
};

/**
 * Actions du store.
 */
const actions: ActionTree<CriteriaState, RootState> = {
};

export const CriteriaStore: Module<CriteriaState, RootState> = {
    state: new CriteriaState(),
    getters,
    mutations,
    actions,
    namespaced: false
};
